import { NgModule } from '@angular/core';
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { AppComponent } from './app.component';
import { RoutesModule } from './routes.module';
import { SharedModule } from './shared/shared.module';
import { provideToastr } from 'ngx-toastr';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DefaultLayoutComponent } from '../app/layout/default-layout/default-layout.component';
import { SidebarComponent } from '../app/layout/sidebar/sidebar.component';
import { AccordionDirective } from '../app/layout/sidebar/accordion.directive';
import { AccordionItemDirective } from '../app/layout/sidebar/accordionItem.directive';
import { AccordionAnchorDirective } from '../app/layout/sidebar/accordionanchor.directive';
import { NavbarComponent } from '../app/layout/navbar/navbar.component';
import { FooterComponent } from '../app/layout/footer/footer.component';
import { TranslateComponent } from '../app/layout/widgets/translate.component';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { AppTimeoutComponent, TimeoutDialog } from './layout/widgets/timeout.component';
import { environment } from '@env/environment';
import { BASE_URL } from '@shared/core/interceptors/base-url-interceptor';
import { appInitializerProviders } from '@shared/core/initializers';
import { EditorModule } from "@progress/kendo-angular-editor";
import { InputsModule } from "@progress/kendo-angular-inputs";
import { ButtonsModule } from "@progress/kendo-angular-buttons";
import { LabelModule } from "@progress/kendo-angular-label";

import { IPublicClientApplication, PublicClientApplication, InteractionType, LogLevel, BrowserCacheLocation } from '@azure/msal-browser';
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule, MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalRedirectComponent
} from '@azure/msal-angular';

import { b2cPolicies, loginRequest, msalConfig, protectedResources } from './shared/auth/auth-config';
import { ConfirmDialogComponent } from './shared/components/confirm-dialog/confirm-dialog.component';
import { PDFExportModule } from '@progress/kendo-angular-pdf-export';
import { AccountInformationComponent } from './shared/components/account-information/account-information.component';
import { httpInterceptorProviders } from '@shared/core/interceptors';
import { MatMenuModule } from '@angular/material/menu';
import { AuthService } from '@shared/auth/auth.service';
import { LicenseModule } from './components/license/license.module';
import { ResidentialHistoryModule } from './components/residential-history/residential-history.module';
import { MainMenuRoutingModule } from './components/mainMenu/mainMenuRouting.module';
import { AdminRoutingModule } from './components/admin/adminRouting.module';

import { BusinessRoutingModule } from './components/business/businessRouting.component';
import { ComplaintRoutingModule } from './components/complaint/complaintRouting.component';
// import { BusinessInfoComponent } from './components/business/businessInfo/businessInfo.component';
import { SearchComponent } from './components/search/search.component';
import { PublicComplaintComponent } from './components/public-complaint/public-complaint.component';
import { DatePipe } from '@angular/common';
import { HelpComponent, RedirectGuard } from './components/help/help.component';
import { AccessibilityNoticeComponent } from './components/public-complaint/accessibilityNotice/accessibilityNotice.component';
// import { PrintBusinessComponent } from './components/printer/printBusiness/printBusiness.component';
// import { PrinterComponent } from './components/printer/printer.component';
// import { BusinessInfoComponent } from './components/complaint/complaintBusinessInfo/complaintBusinessInfo.component';


const isIE = window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1;

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  if(environment.auth.defaultAuth == AuthService.b2cAuthType) {
    return new PublicClientApplication({
      auth: {
        clientId: environment.auth.b2c.clientId,
        authority: b2cPolicies.authorities.signUpSignIn.authority,
        redirectUri: environment.auth.b2c.redirectUri,
        postLogoutRedirectUri: environment.auth.b2c.postLogoutRedirectUri,
        knownAuthorities: [b2cPolicies.authorityDomain]
      },
      cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage,
        storeAuthStateInCookie: isIE, // set to true for IE 11
      },
      system: {
        loggerOptions: {
          loggerCallback,
          logLevel: LogLevel.Warning,
          piiLoggingEnabled: true
        }
      }
    });
  }
  else if(environment.auth.defaultAuth == AuthService.aadAuthType) {
    return new PublicClientApplication(msalConfig);
  }

  console.log('Invalid Auth Type: ' + environment.auth.defaultAuth);
  return null;
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  if(environment.auth.defaultAuth == AuthService.b2cAuthType) {
    protectedResourceMap.set(protectedResources.endpoint, protectedResources.scopes);

    return {
      interactionType: InteractionType.Redirect,
      protectedResourceMap
    };

  }
  else if(environment.auth.defaultAuth == AuthService.aadAuthType) {

    protectedResourceMap.set(protectedResources.endpoint, protectedResources.scopes);

    return {
      interactionType: InteractionType.Redirect,
      protectedResourceMap
    };
  }
  console.log('Invalid Auth Type: ' + environment.auth.defaultAuth);
  return null;
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  if(environment.auth.defaultAuth == AuthService.b2cAuthType) {
    return {
      interactionType: InteractionType.Redirect,
      authRequest: {
        scopes: [...protectedResources.scopes]
      },
      loginFailedRoute: 'login-failed'
    };
  }
  else if(environment.auth.defaultAuth == AuthService.aadAuthType) {
    return {
      interactionType: InteractionType.Redirect,
      authRequest: loginRequest
    };
  }
  console.log('Invalid Auth Type: ' + environment.auth.defaultAuth);
  return null;
}

// Required for AOT compilation
export function TranslateHttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '../assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AppComponent,
    // PrintBusinessComponent,
    // PrinterComponent,
    DefaultLayoutComponent,
    TimeoutDialog,
    AppTimeoutComponent,
    SidebarComponent,
    AccordionDirective,
    AccordionItemDirective,
    AccordionAnchorDirective,
    NavbarComponent,
    FooterComponent,
    TranslateComponent,
    ConfirmDialogComponent,
    AccountInformationComponent,
    SearchComponent,
    PublicComplaintComponent,
    AccessibilityNoticeComponent,
    HelpComponent
    // BusinessInfoComponent
  ],
  imports: [
    BrowserModule,
    NgIdleKeepaliveModule.forRoot(),
    BrowserAnimationsModule,
    HttpClientModule,
    RoutesModule,
    SharedModule,
    MsalModule,
    MatMenuModule,
    MainMenuRoutingModule,
    BusinessRoutingModule,
    ComplaintRoutingModule,
    AdminRoutingModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: TranslateHttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    PDFExportModule,
    EditorModule,
    LicenseModule,
    ResidentialHistoryModule
  ],
  providers: [
    provideAnimations(),
    provideToastr(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    RedirectGuard,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    DatePipe,
    { provide: BASE_URL, useValue: environment.baseUrl },
    httpInterceptorProviders,
    appInitializerProviders
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
